import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  output,
  effect,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { PopupPosition } from 'src/app/components/copy-button/copy-button.component';
import { SensitiveDataContentService } from '../../sensitive-data-content.service';
import { LucideAngularModule } from 'lucide-angular';
import { SensitiveDataType, ActivityMessage, SensitiveDataSource } from '../activity-message.types';
import { ThreadActivitiesStepContent } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-finding-thread-response.model';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import {
  formatToolInvocationParams,
  formatToolInvocationResult,
  prettyMessageAsStringAsync,
} from 'src/app/common/prettyUtils';
import { Label } from 'src/app/labels/englishLabels';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';

@Component({
  selector: 'app-sensitive-message-content',
  standalone: true,
  templateUrl: './sensitive-message-content.component.html',
  styleUrls: ['./sensitive-message-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatSlideToggleModule,
    LabelsProviderPipe,
    MatTooltipModule,
    MatProgressSpinnerModule,
    LucideAngularModule,
  ],
})
export class SensitiveMessageContentComponent implements OnInit, OnDestroy {
  public id = input.required<string>();
  public sensitiveDataParams = input.required<Required<ActivityMessage>['sensitiveData']>();
  public sensitiveDataIndexes = input<string>();

  public sensitiveDataLoaded = output<string[]>();

  public sensitiveDataItems = signal<string[]>([]);
  public stepError = signal<string | null>(null);

  protected stepErrorMessage = computed(() => {
    const error = this.stepError();
    if (!error) return '';
    return Label.get('enterpriseAIThreads.stepContentErrorGeneric') ?? '';
  });

  protected sensitiveIconToolTipLabelPath = 'aiAgentStepTranscriptDialog.sensitiveIconToolTip';
  protected PopupPosition = PopupPosition;

  protected sensitiveDataExpanded = signal(false);
  protected isContentCollapsible = signal(false);

  private readonly sensitiveDataElement = viewChild<ElementRef<HTMLDivElement>>('sensitiveData');
  private readonly _sensitiveBlockSubject = new BehaviorSubject<boolean>(false);
  private readonly MAX_COLLAPSED_HEIGHT = 204;
  private _resizeObserver: ResizeObserver | null = null;
  private _hasCheckedCollapsible = false;

  constructor(
    protected readonly _sensitiveDataContentService: SensitiveDataContentService,
    private readonly _destroyRef: DestroyRef,
    protected readonly _permissionsService: PermissionsService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _logger: LoggerService
  ) {
    effect(() => {
      const element = this.sensitiveDataElement();
      const items = this.sensitiveDataItems();

      if (element?.nativeElement && items.length > 0) {
        this._setupResizeObserver(element.nativeElement);
      }
    });

    effect(() => {
      const error = this.stepError();
      if (error) {
        this._logger.warn(`Step content retrieval failed: ${error}`);
      }
    });
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
  }

  private _setupResizeObserver(element: HTMLDivElement): void {
    this._resizeObserver?.disconnect();

    this._resizeObserver = new ResizeObserver(() => {
      const scrollHeight = element.scrollHeight;
      if (scrollHeight > 0 && !this._hasCheckedCollapsible) {
        this._hasCheckedCollapsible = true;
        this.isContentCollapsible.set(scrollHeight > this.MAX_COLLAPSED_HEIGHT);
        this._cdr.markForCheck();
      }
    });

    this._resizeObserver.observe(element);
  }

  ngOnInit(): void {
    this._sensitiveDataContentService
      .observeSensitiveDataForId(this.id())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(async (stepResult) => {
        if (!stepResult) return;
        if (stepResult.error) {
          this.stepError.set(stepResult.error);
          return;
        }
        this.stepError.set(null);
        if (stepResult.content) {
          await this._parseSensitiveDataItemsFromContent(stepResult.content);
          this.sensitiveDataLoaded.emit(this.sensitiveDataItems());
          this._sensitiveBlockSubject.next(true);
        }
      });
  }

  private async _parseSensitiveDataItemsFromContent(content: ThreadActivitiesStepContent): Promise<void> {
    const sensitiveDataParams = this.sensitiveDataParams();
    switch (sensitiveDataParams.type) {
      case SensitiveDataType.SNIPPET: {
        const transcript = content.transcript;
        if (!transcript) break;
        const sensitiveDataIndexes = this.sensitiveDataIndexes();
        if (sensitiveDataIndexes) {
          const items: string[] = [];
          const sortedIndexes = sensitiveDataIndexes
            .split(',')
            .map((str) => parseInt(str.trim(), 10))
            .sort();

          if (sortedIndexes[0] !== 0) {
            sortedIndexes.unshift(0);
          }

          if (sortedIndexes[sortedIndexes.length - 1] !== transcript.length) {
            sortedIndexes.push(transcript.length);
          }

          for (let currentIdx = 0; currentIdx < sortedIndexes.length - 1; currentIdx++) {
            const startIdx = sortedIndexes[currentIdx];
            const endIdx = sortedIndexes[currentIdx + 1];
            const formatted = await this._formatContent(transcript.substring(startIdx, endIdx));
            items.push(formatted);
          }
          this.sensitiveDataItems.set(items);
        } else {
          this.sensitiveDataItems.set([await this._formatContent(transcript)]);
        }
        break;
      }
      case SensitiveDataType.SOURCE: {
        const fileName = (sensitiveDataParams as SensitiveDataSource).fileName;
        const sourceContent = content.snippet?.[fileName];
        if (sourceContent) {
          this.sensitiveDataItems.set([await this._formatContent(sourceContent)]);
        }
        break;
      }
      case SensitiveDataType.TOOL_INVOCATION_PARAMS:
      case SensitiveDataType.TOOL_INVOCATION_RESULT: {
        const isParams = sensitiveDataParams.type === SensitiveDataType.TOOL_INVOCATION_PARAMS;
        const paramOrResult = isParams ? 'params' : 'result';
        const rawResourceId = sensitiveDataParams.resourceId;
        const resourceId =
          !rawResourceId || rawResourceId === '***' ? Object.keys(content.toolInvocation ?? {})[0] : rawResourceId;
        const invocation = content.toolInvocation?.[resourceId];
        const sensitiveDataBody = invocation?.[paramOrResult];
        if (sensitiveDataBody) {
          const toolFormatter = isParams ? formatToolInvocationParams : formatToolInvocationResult;
          const formattedBody = toolFormatter(sensitiveDataBody);
          this.sensitiveDataItems.set([await this._formatContent(formattedBody)]);
        }
        break;
      }
    }
  }

  private async _formatContent(content: string): Promise<string> {
    if (!content) {
      return content;
    }

    try {
      const result = await prettyMessageAsStringAsync(content);
      return result.text;
    } catch {
      return content.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
    }
  }
}
